import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { workflowApi } from "../../../api/workflow";
import { apiEndpoints } from "../../../api/endpoints";
import { v4 as uuidv4 } from "uuid";

import Navbar from "../../layout/Navbar";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ScreenshotPreview from "../../sections/ScreenshotPreview";
import DataFixture from "../../sections/DataFixture";
import StepScreenshot from "../../sections/StepScreenshot";

const TEST_ACTIONS = [
  {
    value: "type",
    label: "Type Text",
    fields: [
      {
        name: "value",
        label: "Text to type",
        placeholder: "Enter text to type",
      },
      {
        name: "element",
        label: "Element to type into",
        placeholder: "Type name of input here",
      },
    ],
  },
  {
    value: "click",
    label: "Click Element",
    fields: [
      {
        name: "element",
        label: "Element to click",
        placeholder: "Type element to click",
      },
    ],
  },
  {
    value: "navigate",
    label: "Navigate to URL",
    fields: [
      { name: "value", label: "URL", placeholder: "Enter URL to navigate to" },
    ],
  },
  {
    value: "select",
    label: "Select Option",
    fields: [
      {
        name: "value",
        label: "Option to Select",
        placeholder: "Enter option text or value",
      },
      {
        name: "element",
        label: "Dropdown Element",
        placeholder: "Type name of dropdown",
      },
    ],
  },
  {
    value: "enter",
    label: "Press Enter",
    fields: [],
  },
  {
    value: "reload",
    label: "Reload Page",
    fields: [],
  },
];

function EditWorkflow() {
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    workflow_type: "regular",
    currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
    savedSteps: [],
    editingStepIndex: undefined,
  });
  const [isLoading, setIsLoading] = useState(true);

  const { workflowId } = useParams();
  const [suiteId, setSuiteId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [initialScreenshot, setInitialScreenshot] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [testStatus, setTestStatus] = useState(null);
  const pollIntervalRef = useRef(null);
  const navigate = useNavigate();
  const [isFixtureModalOpen, setIsFixtureModalOpen] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);

  // Screenshot-related state
  const [stepScreenshot, setStepScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);

  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        setIsLoading(true);
        const workflow = await workflowApi.getWorkflowById(workflowId);
        setSuiteId(workflow.test_suite_id);

        const parsedSteps = workflow.steps
          .map((step) => {
            return {
              id: step.id,
              workflow_id: step.workflow_id,
              order: step.step_order,
              name: step.name,
              action: step.metadata.actionType,
              value: step.metadata.value || "",
              element: step.metadata.selector || "",
              exact: step.metadata.exact || false,
              secret: step.metadata.secret || false,
              value_mode: step.metadata.value_mode || "static",
              metadata: step.metadata || {},
              created_at: step.created_at,
              updated_at: step.updated_at,
            };
          })
          .sort((a, b) => a.order - b.order);

        setFormData({
          name: workflow.name,
          description: workflow.description || "",
          workflow_type: workflow.workflow_type || "regular",
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
          savedSteps: parsedSteps,
          editingStepIndex: undefined,
        });
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching workflow:", error);
        setError("Failed to load workflow");
        setIsLoading(false);
      }
    };

    if (workflowId) {
      fetchWorkflow();
    }
  }, [workflowId]);

  useEffect(() => {
    const initializeBrowserSession = async () => {
      try {
        const result = await apiEndpoints.startBrowserSession(suiteId);
        setSessionId(result.sessionId);

        if (result.screenshot?.data) {
          setInitialScreenshot(`data:image/jpeg;base64,${result.screenshot.data}`);        }
      } catch (error) {
        console.error("Failed to initialize browser session:", error);
        setError("Failed to start browser session: " + error.message);
      }
    };

    if (suiteId) {
      initializeBrowserSession();
    }
  }, [suiteId]);

  const handleResetScreenshot = async () => {
    try {
      const result = await apiEndpoints.startBrowserSession(suiteId);
      setSessionId(result.sessionId);

      if (result.screenshot?.data) {
        setInitialScreenshot(`data:image/jpeg;base64,${result.screenshot.data}`);
        setScreenshots([])
      }
    } catch (error) {
      console.error("Failed to reset browser session:", error);
      setError("Failed to reset browser session: " + error.message);
    }
  };

  const handleRunAllSteps = async () => {
    if (isPolling) {
      try {
        await apiEndpoints.cancelBrowserSession(sessionId);
        
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        setIsPolling(false);
        
        setTestStatus({
          status: "completed",
          description: "Test execution cancelled by user"
        });
        
      } catch (error) {
        console.error("Error cancelling test execution:", error);
        setError(`Failed to cancel test execution: ${error.message}`);
      }
      return;
    }

    openFixtureModal();
  };

  const openFixtureModal = () => {
    setIsFixtureModalOpen(true);
  };

  const closeFixtureModal = () => {
    setIsFixtureModalOpen(false);
  };

  const handleFixtureSelect = (fixture) => {
    setSelectedFixture(fixture);
    runSteps(fixture?.id);
  };

  const handleRunWithoutFixture = () => {
    setSelectedFixture(null);
    runSteps();
  };

  const runSteps = async (fixtureId = null) => {
    try {
      if (!sessionId) {
        setError("No active browser session");
        return;
      }

      if (formData.savedSteps.length === 0) {
        setError("Please add at least one step first");
        return;
      }

      setIsPolling(true);
      setTestStatus(null);
      
      const formattedSteps = formData.savedSteps.map((step) => {
        if (step.action === 'workflow') {
          return {
            action: step.action,
            workflowId: step.workflowId
          };
        }

        if (step.action === 'click') {
          let elementValue = step.element || '';
          
          if (step.exact) {
            const bracketMatch = elementValue.match(/\{\{([^}]+)\}\}/);
            if (bracketMatch && bracketMatch[1]) {
              elementValue = bracketMatch[1].trim();
            }
          }
          
          return {
            action: step.action,
            element: elementValue || undefined,
            exact: step.exact || false,
            ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })

          };
        }

        if (step.action === 'type') {
          return {
            action: step.action,
            element: step.element || undefined,
            value: step.value || undefined,
            secret: step.secret || false,
            value_mode: step.value_mode || 'static',
            ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })

          };
        }

        if (step.action === 'navigate') {
          return {
            action: step.action,
            value: step.value || undefined,
            value_mode: step.value_mode || "static"
          };
        }

        if (step.action === 'reload') {
          return {
            action: step.action
          };
        }

        return {
          action: step.action,
          element: step.element || undefined,
          value: step.value || undefined,
          ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })

        };
      });
      
      await apiEndpoints.executeBrowserSteps(
        formattedSteps,
        suiteId,
        sessionId,
        fixtureId
      );
      startBatchStepsPolling();
    } catch (error) {
      console.error("Error running steps:", error);
      setError(`Failed to run steps: ${error.message}`);
      setIsPolling(false);
    }
  };

  const startBatchStepsPolling = () => {
    if (isPolling) return;
  
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  
    setIsPolling(true);
    
    setScreenshots([]); 

    // Store the current step index to track progress
    let currentStepIndex = 0;
  
    pollIntervalRef.current = setInterval(async () => {
      try {
        const result = await apiEndpoints.getBatchStepsResult(sessionId);
        const { status, screenshot, current_step } = result;
  
        if (
          status === "completed" ||
          status === "error" ||
          status === "failed"
        ) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setIsPolling(false);
          setTestStatus({
            status,
            description:
              status === "error"
                ? result.error || "Steps failed"
                : "All steps completed successfully",
          });
  
          if (screenshot?.data) {
            // Add final screenshot if it's new
            if (current_step > currentStepIndex) {
              setScreenshots((prev) => [...prev, `data:image/jpeg;base64,${screenshot.data}`]);
            } else {
              // Update the last screenshot
              setScreenshots((prev) => {
                const newScreenshots = [...prev];
                if (newScreenshots.length > 0) {
                  newScreenshots[newScreenshots.length - 1] = `data:image/jpeg;base64,${screenshot.data}`;
                } else {
                  newScreenshots.push(`data:image/jpeg;base64,${screenshot.data}`);
                }
                return newScreenshots;
              });
            }
          }
        } else if (status === "running" && screenshot?.data) {
          // Check if we've moved to a new step
          if (current_step && current_step > currentStepIndex) {
            // We're on a new step, add a new screenshot
            setScreenshots((prev) => [...prev, `data:image/jpeg;base64,${screenshot.data}`]);
            currentStepIndex = current_step;
          } else {
            // We're still on the same step, update the last screenshot
            setScreenshots((prev) => {
              const newScreenshots = [...prev];
              if (newScreenshots.length > 0) {
                newScreenshots[newScreenshots.length - 1] = `data:image/jpeg;base64,${screenshot.data}`;
              } else {
                newScreenshots.push(`data:image/jpeg;base64,${screenshot.data}`);
              }
              return newScreenshots;
            });
          }
        }
      } catch (error) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setIsPolling(false);
        setError("Failed to get batch steps result: " + error.message);
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Handler for the password checkbox
  const handleToggleSecret = () => {
    setFormData(prev => ({
      ...prev,
      currentStep: {
        ...prev.currentStep,
        secret: !prev.currentStep.secret
      }
    }));
  };

  // Handler for value mode selection
  const handleValueModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      currentStep: {
        ...prev.currentStep,
        value_mode: mode
      }
    }));
  };



  // Handler to remove existing screenshot when editing a step
  const handleRemoveExistingScreenshot = () => {
    if (formData.editingStepIndex !== undefined) {
      // We're editing a step, so update both currentStep and the step in savedSteps
      setFormData(prev => {
        const updatedSteps = [...prev.savedSteps];
        const currentStep = updatedSteps[prev.editingStepIndex];
        
        // Remove screenshot from step metadata
        if (currentStep && currentStep.metadata) {
          currentStep.metadata = {
            ...currentStep.metadata,
            step_screenshot: undefined
          };
          delete currentStep.metadata.step_screenshot;
        }
        
        // Also update the current step being edited
        const updatedCurrentStep = {
          ...prev.currentStep,
          metadata: {
            ...prev.currentStep.metadata,
            step_screenshot: undefined
          }
        };
        delete updatedCurrentStep.metadata.step_screenshot;
        
        return {
          ...prev,
          savedSteps: updatedSteps,
          currentStep: updatedCurrentStep
        };
      });
    }
  };

  const handleEditStep = (index) => {
    const stepToEdit = { ...formData.savedSteps[index] };

    if (stepToEdit.action === 'click' && stepToEdit.exact) {
      if (!stepToEdit.element.match(/^\{\{.*\}\}$/)) {
        stepToEdit.element = `{{${stepToEdit.element}}}`;
      }
    }

    // Extract screenshot from metadata if present
    if (stepToEdit.metadata && stepToEdit.metadata.step_screenshot) {
      setStepScreenshot(stepToEdit.metadata.step_screenshot);
      setScreenshotPreview(stepToEdit.metadata.step_screenshot);
    } else {
      setStepScreenshot(null);
      setScreenshotPreview(null);
    }

    setFormData((prev) => ({
      ...prev,
      currentStep: {
        action: stepToEdit.action || stepToEdit.metadata?.actionType || "type",
        value: stepToEdit.value || stepToEdit.metadata?.value || "",
        element: stepToEdit.element || stepToEdit.metadata?.selector || "",
        exact: stepToEdit.exact || stepToEdit.metadata?.exact || false,
        secret: stepToEdit.secret || stepToEdit.metadata?.secret || false,
        value_mode: stepToEdit.value_mode || stepToEdit.metadata?.value_mode || "static"
      },
      editingStepIndex: index,
    }));

    const addStepSection = document.getElementById("add-step-section");
    if (addStepSection) {
      addStepSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSaveStep = () => {
    const { action, value, element, exact, secret, value_mode } = formData.currentStep;

    if (formData.editingStepIndex !== undefined) {
      if (action === "navigate" && !value) {
        setError("Please enter a URL");
        return;
      }

      if (
        (action === "click" || action === "type" || action === "select") &&
        !element
      ) {
        setError(`Please enter an element for the ${action} action`);
        return;
      }

      if ((action === "type" || action === "select") && !value) {
        setError(`Please enter a value for the ${action} action`);
        return;
      }

      const processedElement = action === "click" ? 
        processElement(element, exact) : element;

      const stepName = formatStepName({ 
        action, 
        value, 
        element: processedElement,
        exact,
        secret,
        value_mode
      });

      // Handle screenshot URL if present
      let screenshotUrl = null;
      if (stepScreenshot) {
        // Check if stepScreenshot is a URL string or file object
        screenshotUrl = typeof stepScreenshot === 'string' ? stepScreenshot : stepScreenshot?.uploadedUrl;
      }

      setFormData((prevState) => {
        const updatedSteps = [...prevState.savedSteps];
        updatedSteps[prevState.editingStepIndex] = {
          ...updatedSteps[prevState.editingStepIndex],
          action,
          value: action === "reload" ? "" : value,
          element: action === "reload" ? "" : processedElement,
          exact: action === "click" ? exact : false,
          secret: action === "type" ? secret : false,
          value_mode: action === "type" ? value_mode : (action === "navigate" ? value_mode : "static"),
          name: stepName,
          metadata: {
            actionType: action,
            ...(action !== "reload" && { selector: processedElement || undefined }),
            ...(action !== "reload" && { value: value || undefined }),
            ...(action === "click" && { exact }),
            ...(action === "type" && { 
              secret,
              value_mode
            }),
            ...(action === "navigate" && { 
              value_mode: value_mode || "static"
            }),
            ...(screenshotUrl && { step_screenshot: screenshotUrl })
          },
          updated_at: new Date().toISOString(),
        };

        return {
          ...prevState,
          savedSteps: updatedSteps,
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
          editingStepIndex: undefined,
        };
      });
      
      // Clear screenshot after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      setError(null);
      return;
    }

    const nextOrder = formData.savedSteps.length + 1;

    if (action === "enter") {
      setFormData((prevState) => ({
        ...prevState,
        savedSteps: [
          ...prevState.savedSteps,
          {
            id: uuidv4(),
            workflow_id: workflowId,
            order: nextOrder,
            name: "Press Enter key",
            action: "enter",
            value: "",
            element: "",
            exact: false,
            secret: false,
            value_mode: "static",
            metadata: {
              actionType: "enter",
            },
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ],
        currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
      }));
      return;
    }

    if (action === "reload") {
      setFormData((prevState) => ({
        ...prevState,
        savedSteps: [
          ...prevState.savedSteps,
          {
            id: uuidv4(),
            workflow_id: workflowId,
            order: nextOrder,
            name: "Reload page",
            action: "reload",
            value: "",
            element: "",
            exact: false,
            secret: false,
            value_mode: "static",
            metadata: {
              actionType: "reload",
            },
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ],
        currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
      }));
      return;
    }

    if (action === "navigate" && !value) {
      setError("Please enter a URL");
      return;
    }

    if (action === "click" && !element) {
      setError("Please specify an element to click");
      return;
    }

    if (
      !action ||
      (!value && action !== "click" && action !== "enter" && action !== "reload") ||
      (!element && action !== "navigate" && action !== "enter" && action !== "reload")
    ) {
      setError("Please fill in all required fields for this step");
      return;
    }

    const processedElement = action === "click" ? 
      processElement(element, exact) : element;

    let stepName = "";
    switch (action) {
      case "type":
        stepName = `Type "${value}" into ${processedElement}`;
        break;
      case "click":
        stepName = `Click on ${processedElement}`;
        break;
      case "navigate":
        let navValueModeIndicator = "";
        if (value_mode === "fixture") {
          navValueModeIndicator = " (fixture)";
        }
        stepName = `Navigate to ${value}${navValueModeIndicator}`;
        break;
      case "select":
        stepName = `Select "${value}" from "${processedElement}"`;
        break;
      case "reload":
        stepName = "Reload page";
        break;
      default:
        stepName = `${action} ${processedElement}`;
    }

    // Handle screenshot URL if present
    let screenshotUrl = null;
    if (stepScreenshot) {
      // Check if stepScreenshot is a URL string or file object
      screenshotUrl = typeof stepScreenshot === 'string' ? stepScreenshot : stepScreenshot?.uploadedUrl;
    }

    setFormData((prevState) => ({
      ...prevState,
      savedSteps: [
        ...prevState.savedSteps,
        {
          id: uuidv4(),
          workflow_id: workflowId,
          order: nextOrder,
          name: stepName,
          action,
          value: action === "reload" ? "" : value,
          element: action === "reload" ? "" : processedElement,
          exact: action === "click" ? exact : false,
          secret: action === "type" ? secret : false,
          value_mode: action === "type" ? value_mode : (action === "navigate" ? value_mode : "static"),
          metadata: {
            actionType: action,
            ...(action !== "reload" && { selector: processedElement }),
            ...(action !== "reload" && { value: value }),
            ...(action === "click" && { exact }),
            ...(action === "type" && { 
              secret,
              value_mode
            }),
            ...(action === "navigate" && { 
              value_mode: value_mode || "static"
            }),
            ...(screenshotUrl && { step_screenshot: screenshotUrl })
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ],
      currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
    }));

    // Clear screenshot after saving
    setStepScreenshot(null);
    setScreenshotPreview(null);
    setError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveStep();
    }
  };

  const handleRemoveStep = (index) => {
    setFormData((prev) => ({
      ...prev,
      savedSteps: prev.savedSteps.filter((_, i) => i !== index),
    }));
  };

  const processElement = (text, isExact) => {
    if (!text) return '';
    const cleanText = text.replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
    return isExact ? `{{${cleanText}}}` : cleanText;
  };

  // Helper function to format step names for display
  const formatStepNameForDisplay = (step) => {
    switch (step.action) {
      case "type":
        // Mask the value if the element is a password field or if secret is true
        const shouldMaskValue = step.element?.toLowerCase().includes('password') || step.secret === true;
        const displayValue = shouldMaskValue ? '••••••••' : step.value;
        
        // Add value mode indicator
        let valueModeIndicator = "";
        if (step.value_mode === "fixture") {
          valueModeIndicator = " (fixture)";
        } else if (step.value_mode === "random") {
          valueModeIndicator = " (random)";
        }
        
        return `Type "${displayValue}" into ${step.element}`;
      case "click":
        const displayElement = step.exact ? 
          `{{${step.element.replace(/^\{\{|\}\}$/g, '')}}}` : 
          step.element;
        return `Click on ${displayElement}`;
      case "navigate":
        let navValueModeIndicator = "";
        if (step.value_mode === "fixture") {
          navValueModeIndicator = " (fixture)";
        }
        return `Navigate to ${step.value}${navValueModeIndicator}`;
      case "select":
        return `Select "${step.value}" from "${step.element}"`;
      case "enter":
        return `Press Enter key`;
      case "reload":
        return `Reload page`;
      default:
        return `${step.action} ${step.element || step.value || ""}`;
    }
  };

  // Original formatStepName function for API use (no masking)
  const formatStepName = (step) => {
    switch (step.action) {
      case "type":
        let valueModeIndicator = "";
        if (step.value_mode === "fixture") {
          valueModeIndicator = " (fixture)";
        } else if (step.value_mode === "random") {
          valueModeIndicator = " (random)";
        }
        return `Type "${step.value}" into ${step.element}`;
      case "click":
        const displayElement = step.exact ? 
          `{{${step.element.replace(/^\{\{|\}\}$/g, '')}}}` : 
          step.element;
        return `Click on ${displayElement}`;
      case "navigate":
        let navModeIndicator = "";
        if (step.value_mode === "fixture") {
          navModeIndicator = " (fixture)";
        }
        return `Navigate to ${step.value}${navModeIndicator}`;
      case "select":
        return `Select "${step.value}" from "${step.element}"`;
      case "enter":
        return `Press Enter key`;
      case "reload":
        return `Reload page`;
      default:
        return `${step.action} ${step.element || step.value || ""}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Please provide a workflow name");
      return;
    }

    if (formData.savedSteps.length === 0) {
      alert("Please add at least one step");
      return;
    }

    try {
      const formattedSteps = formData.savedSteps.map((step) => ({
        order: step.order,
        existingStepId: step.id.toString().startsWith("temp-") ? null : step.id,
        newStepData: {
          name: step.name,
          metadata: {
            ...step.metadata,
            // Make sure secret flag is included for type actions
            ...(step.action === "type" && { secret: step.secret })
          },
        },
        subWorkflowId: null,
      }));

      const workflowData = {
        name: formData.name,
        description: formData.description || "No description provided",
        workflow_type: formData.workflow_type,
        steps: formattedSteps,
      };

      await workflowApi.updateWorkflow(workflowId, workflowData);
      navigate(-1);
    } catch (error) {
      console.error("Error updating workflow:", error);
      setError("Failed to update workflow");
    }
  };

  const handleDragEnd = (result) => {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    const newSteps = Array.from(formData.savedSteps);

    const [movedItem] = newSteps.splice(result.source.index, 1);

    newSteps.splice(result.destination.index, 0, movedItem);

    const reorderedSteps = newSteps.map((step, index) => ({
      ...step,
      order: index + 1,
    }));

    setFormData((prev) => ({
      ...prev,
      savedSteps: reorderedSteps,
    }));
  };

  const renderStep = (step, index, provided, snapshot) => (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 ${
        snapshot.isDragging ? "bg-blue-50" : ""
      }`}
    >
      <div className="flex-1 flex items-center">
        <div
          {...provided.dragHandleProps}
          className="mr-2 cursor-grab text-gray-400 hover:text-gray-600"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">
              {index + 1}.
            </span>
            <span className="text-sm font-medium text-gray-900">
              {formatStepNameForDisplay(step)}
            </span>
          </div>
          
          {/* Display password indicator for secret fields */}
          {step.action === "type" && step.secret && (
            <div className="ml-5 mt-1 text-xs text-blue-600 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Password field (value masked)
            </div>
          )}

          {step.action === "navigate" && (
            <div className="mt-1 text-xs text-gray-500">
              URL: {step.value}
              {step.value_mode === "fixture" && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 py-0.5 px-1.5 rounded">
                  fixture
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleEditStep(index)}
          className="text-blue-600 hover:text-blue-800 p-1"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => handleRemoveStep(index)}
          className="text-red-600 hover:text-red-800 p-1"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  const renderStepForm = () => {
    return (
      <div id="add-step-section" className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          {formData.editingStepIndex !== undefined
            ? "Edit Step"
            : "Add New Step"}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Action
            </label>
            <select
              value={formData.currentStep.action}
              onChange={(e) => {
                const newAction = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  currentStep: {
                    ...prev.currentStep,
                    action: newAction,
                    exact: newAction === 'click' ? prev.currentStep.exact : false,
                    secret: newAction === 'type' ? prev.currentStep.secret : false
                  },
                }))
              }}
              className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
            >
              {TEST_ACTIONS.map((action) => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))}
            </select>
          </div>

          {TEST_ACTIONS.find(
            (a) => a.value === formData.currentStep.action
          )?.fields.map((field) => (
            <div key={field.name}>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {field.label}
              </label>
              <div className="flex">
                <input
                  type={formData.currentStep.secret && field.name === 'value' && formData.currentStep.action === 'type' ? "password" : "text"}
                  value={formData.currentStep[field.name] || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    
                    if (formData.currentStep.action === 'click' && field.name === 'element') {
                      const hasExactBrackets = newValue && /^\{\{.*\}\}$/.test(newValue.trim());
                      
                      setFormData(prev => ({
                        ...prev,
                        currentStep: { 
                          ...prev.currentStep, 
                          [field.name]: newValue,
                          exact: hasExactBrackets
                        }
                      }));
                    } else if (formData.currentStep.action === 'navigate' && field.name === 'value') {
                      // Check if value is in {fixture} format and auto-set value_mode to fixture
                      const isFixtureFormat = newValue && /^\{[^{}]+\}$/.test(newValue.trim());
                      
                      setFormData(prev => ({
                        ...prev,
                        currentStep: { 
                          ...prev.currentStep, 
                          [field.name]: newValue,
                          value_mode: isFixtureFormat ? 'fixture' : prev.currentStep.value_mode
                        }
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        currentStep: { ...prev.currentStep, [field.name]: newValue }
                      }));
                    }
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder={field.placeholder}
                  className={`${
                    formData.currentStep.action === "click" && field.name === "element"
                      ? "rounded-l"
                      : formData.currentStep.action === "type" && field.name === "value"
                      ? "rounded-l"
                      : "rounded"
                  } w-full p-2 border border-gray-300 focus:border-black focus:outline-none`}
                />
                {formData.currentStep.action === "click" && field.name === "element" && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        currentStep: {
                          ...prev.currentStep,
                          exact: !prev.currentStep.exact
                        },
                      }));
                    }}
                    className={`${
                      formData.currentStep.exact
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-gray-400 hover:bg-gray-500"
                    } text-white px-3 py-2 rounded-r border-l-0 transition-colors`}
                    title={formData.currentStep.exact ? "Exact match enabled" : "Exact match disabled"}
                  >
                    Exact {formData.currentStep.exact ? 'ON' : 'OFF'}
                  </button>
                )}
                {formData.currentStep.action === "type" && field.name === "value" && (
                  <button
                    type="button"
                    onClick={handleToggleSecret}
                    className={`${
                      formData.currentStep.secret
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "bg-gray-400 hover:bg-gray-500"
                    } text-white px-3 py-2 rounded-r border-l-0 transition-colors flex items-center`}
                    title={formData.currentStep.secret ? "Password field (value masked)" : "Regular text field"}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    {formData.currentStep.secret ? 'Password ON' : 'Password OFF'}
                  </button>
                )}
              </div>
              {formData.currentStep.action === 'type' && field.name === 'element' && formData.currentStep.element?.toLowerCase().includes('password') && !formData.currentStep.secret && (
                <div className="mt-1 text-xs text-amber-600 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Element name contains "password". Consider enabling password masking.
                </div>
              )}
            </div>
          ))}

          {/* Step Screenshot Component */}
          {(formData.currentStep.action === "type" || 
            formData.currentStep.action === "click" || 
            formData.currentStep.action === "select") && (
            <StepScreenshot
              stepScreenshot={stepScreenshot}
              setStepScreenshot={setStepScreenshot}
              screenshotPreview={screenshotPreview}
              setScreenshotPreview={setScreenshotPreview}
              isUploadingScreenshot={isUploadingScreenshot}
              setIsUploadingScreenshot={setIsUploadingScreenshot}
              stepId={formData.editingStepIndex !== undefined ? 
                (formData.savedSteps[formData.editingStepIndex]?.id || `temp-${Date.now()}`) : 
                `temp-${Date.now()}`
              }
              existingScreenshotUrl={formData.currentStep?.metadata?.step_screenshot}
              onRemoveExistingScreenshot={handleRemoveExistingScreenshot}
            />
          )}

          {formData.currentStep.action === "type" && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Value Mode
              </label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => handleValueModeChange("static")}
                  className={`px-3 py-2 rounded text-sm ${
                    formData.currentStep.value_mode === "static"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Static
                </button>
                <button
                  type="button"
                  onClick={() => handleValueModeChange("fixture")}
                  className={`px-3 py-2 rounded text-sm ${
                    formData.currentStep.value_mode === "fixture"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Fixture
                </button>
                {/* <button
                  type="button"
                  onClick={() => handleValueModeChange("random")}
                  className={`px-3 py-2 rounded text-sm ${
                    formData.currentStep.value_mode === "random"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Random
                </button> */}
              </div>
              {formData.currentStep.value_mode !== "static" && (
                <div className="mt-1 text-xs text-blue-600">
                  {formData.currentStep.value_mode === "fixture" 
                    ? "The value will be treated as a fixture name."
                    : "A random value will be generated."
                  }
                </div>
              )}
            </div>
          )}
          
          {formData.currentStep.action === "navigate" && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Value Mode
              </label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => handleValueModeChange("static")}
                  className={`px-3 py-2 rounded text-sm ${
                    formData.currentStep.value_mode === "static"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Static
                </button>
                <button
                  type="button"
                  onClick={() => handleValueModeChange("fixture")}
                  className={`px-3 py-2 rounded text-sm ${
                    formData.currentStep.value_mode === "fixture"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Fixture
                </button>
              </div>
              {formData.currentStep.value_mode === "fixture" && (
                <div className="mt-1 text-xs text-blue-600">
                  The URL value will be treated as a fixture name. Use format: {'{fixture_name}'}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={handleSaveStep}
            className={`w-full font-medium py-2 px-4 rounded transition-colors mb-2 ${
              formData.editingStepIndex !== undefined
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {formData.editingStepIndex !== undefined
              ? "Update Step"
              : "Add Step"}
          </button>

          {formData.editingStepIndex !== undefined && (
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  currentStep: { action: "type", value: "", element: "", exact: false },
                  editingStepIndex: undefined,
                }))
              }
              className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded transition-colors mt-2"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span>Back to Workflows</span>
          </button>
          <h1 className="text-2xl font-bold">Edit Workflow</h1>
        </div>
        <div className="grid grid-cols-2 gap-8">
          <div className="grid  gap-6 ">
            <div className="bg-white rounded-lg shadow-md p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Workflow Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Workflow Type
                  </label>
                  <select
                    name="workflow_type"
                    value={formData.workflow_type}
                    onChange={handleChange}
                    className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                  >
                    <option value="regular">Regular Workflow</option>
                    <option value="post_execution">
                      Post-Execution (Cleanup)
                    </option>
                  </select>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Workflow Steps
                    </label>
                  </div>

                  {formData.savedSteps.length > 0 && (
                    <div className="border rounded-lg overflow-hidden mb-4">
                      <div className="bg-gray-50 px-4 py-2 border-b">
                        <h3 className="text-sm font-medium text-gray-700">
                          Saved Steps
                        </h3>
                      </div>
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="steps-list">
                          {(provided) => (
                            <div
                              className="divide-y"
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {formData.savedSteps.map((step, index) => (
                                <Draggable
                                  key={`step-${step.id || index}`}
                                  draggableId={`step-${step.id || index}`}
                                  index={index}
                                >
                                  {(provided, snapshot) =>
                                    renderStep(step, index, provided, snapshot)
                                  }
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </div>
                  )}

                  {renderStepForm()}
                </div>

                {error && <div className="text-red-600 text-sm">{error}</div>}

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="mt-6 bg-white rounded-lg shadow-md p-4">
      
          <div className="flex flex-col gap-4">
            <ScreenshotPreview
                title="Screenshot Preview"
                isPolling={isPolling}
                screenshots={[initialScreenshot,...screenshots]}
                testStatus={testStatus}
                pollingMessage="Running test case..."
                loadingMessage="Running test steps..."
                successMessage="All test steps completed successfully."
                onReset={handleResetScreenshot}
                onRun={handleRunAllSteps}
                runButtonDisabled={formData.savedSteps.length === 0}
                runButtonText={isPolling ? "Cancel" : "Run from start"}
                runButtonClass={isPolling ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}    
              />
              </div>

            {testStatus?.status === "completed" && (
              <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                ✅ {testStatus.description}
              </div>
            )}

            {testStatus?.status === "failed" && (
              <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                ❌ {testStatus.description}
              </div>
            )}
          </div>
        </div>
      </div>

      <DataFixture 
        isOpen={isFixtureModalOpen}
        onClose={closeFixtureModal}
        onSelect={handleFixtureSelect}
        onRunWithoutFixture={handleRunWithoutFixture}
      />
    </div>
  );
}

export default EditWorkflow;
